import { useAuth } from "@/contexts/AuthContext";
import { ChevronRight } from "@/icons/ChevronRight";
import { UserPlus } from "@/icons/UserPlus";
import { generateAPIUrl } from "@/utils/api-url";
import { Link, useFocusEffect } from "expo-router";
import { fetch as expoFetch } from "expo/fetch";
import { hc } from "hono/client";
import { useCallback, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import type { ApiPersonaType } from "../app/api/persona/[...everything]+api";
import { PersonaAvatar } from "./persona-avatar";
import {
  type OnCloseCallback,
  PersonaModal,
  type UpdatePersonaCallback,
} from "./persona-modal";
import { Text } from "./ui/text";

const personaClient = hc<ApiPersonaType>(generateAPIUrl("/"), {
  fetch: expoFetch as unknown as typeof fetch,
});

async function loadPersonas(token: string, deletedOnly?: boolean) {
  const res = await personaClient.api.persona.$get(
    {
      query: { deleted: deletedOnly ? "true" : "false" },
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  return res.json();
}

type Persona = Awaited<ReturnType<typeof loadPersonas>>["personas"][number];

export function PersonaEditor({
  isInactiveList = false,
}: {
  isInactiveList?: boolean;
}) {
  const { authState } = useAuth();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      async function internalLoadPersonas() {
        if (!authState.token) return;

        setIsLoading(true);
        try {
          const data = await loadPersonas(authState.token, isInactiveList);
          if (isActive) {
            setPersonas(data.personas || []);
          }
        } catch (error) {
          console.error("Error loading personas:", error);
        } finally {
          if (isActive) {
            setIsLoading(false);
          }
        }
      }

      internalLoadPersonas();

      return () => {
        isActive = false;
      };
    }, [authState.token, isInactiveList]),
  );

  const handleSelectPersona = useCallback((persona: Persona) => {
    setSelectedPersona(persona);
  }, []);

  const selectedPersonaId = selectedPersona?.id;

  const onPersonaModalClose: OnCloseCallback = useCallback(
    (removeFromCurrentScreen) => {
      console.log("removeFromCurrentScreen", removeFromCurrentScreen);
      if (removeFromCurrentScreen && selectedPersonaId) {
        setPersonas((prevPersonas) => {
          return prevPersonas.filter(
            (persona) => persona.id !== selectedPersonaId,
          );
        });
      }
      setSelectedPersona(null);
    },
    [selectedPersonaId],
  );

  const updatePersona: UpdatePersonaCallback = useCallback(
    (updates) => {
      if (selectedPersonaId) {
        setPersonas((prevPersonas) => {
          const updatedPersonas = prevPersonas.map((persona) =>
            persona.id === selectedPersonaId
              ? { ...persona, ...updates }
              : persona,
          );
          return updatedPersonas;
        });

        setSelectedPersona((prevPersona) => {
          if (prevPersona) {
            return { ...prevPersona, ...updates };
          }
          return null;
        });
      }
    },
    [selectedPersonaId],
  );

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" color="#fff" />
      </View>
    );
  }

  if (personas.length === 0) {
    if (isInactiveList) {
      return (
        <View className="flex-1 items-center justify-center p-4">
          <Text className="text-center text-gray-300">
            No archived Personas found.
          </Text>
        </View>
      );
    }
    return (
      <View className="flex-1 items-center justify-center p-4">
        <Text className="text-center text-gray-300">
          No Personas found. Create a Persona to get started.
        </Text>

        <View className="mt-4">
          <AddButton />
        </View>
      </View>
    );
  }

  return (
    <SafeAreaView className="flex-1" edges={["bottom"]}>
      <ScrollView className="flex-1 p-4">
        {personas.map((persona) => (
          <Pressable
            key={persona.id}
            onPress={() => handleSelectPersona(persona)}
            className="mb-3"
          >
            <View className="flex-row items-center justify-between rounded-md bg-card p-4">
              <View className="flex-row items-center">
                <PersonaAvatar persona={persona} className="mr-3" />
                <Text className="text-lg">{persona.name}</Text>
              </View>
              <ChevronRight size={22} className="text-gray-400" />
            </View>
          </Pressable>
        ))}

        <AddButton />
      </ScrollView>

      {selectedPersona && (
        <PersonaModal
          updatePersona={updatePersona}
          persona={selectedPersona}
          isVisible={!!selectedPersona}
          onClose={onPersonaModalClose}
          sourceScreen={
            isInactiveList ? "persona-inactive-list" : "persona-active-list"
          }
        />
      )}
    </SafeAreaView>
  );
}

function AddButton() {
  return (
    <Link href="/persona-import">
      <View className="flex w-full flex-row items-center rounded-full bg-gray-800 p-4">
        <UserPlus color="#fff" className="w-8" />
        <Text className="native:font-bold pl-4">Add Persona</Text>
      </View>
    </Link>
  );
}
